/* eslint-disable */
import axios from 'axios';
import moment from 'moment-timezone';
import _ from 'lodash';
import { safeSnapshot } from '@/store/firestoreListen';
import { 
    collection, 
    query, 
    where, 
    orderBy, 
    getDocs, 
    doc, 
    updateDoc, 
    arrayRemove, 
    runTransaction 
} from 'firebase/firestore';

axios.defaults.baseURL = process.env.VUE_APP_TEAMIO_API_URL;

export default {
  namespaced: true,
  state: {
    notifications: [],
    tempNotifications1: [],
    tempNotifications2: [],
    tempNotifications3: [],
    notificationsLastCleared: localStorage.getItem('notificationsLastCleared'),
    listeners: [] // Added to store listeners
  },

  getters: {
    count: state => state.notifications.length,
    lastCleared: state => state.notificationsLastCleared,
    // Number of unread Kanban @-mentions for the current user. Feeds the
    // Sidebar green-dot indicator on the Kanban nav link.
    kanbanUnreadCount: state => state.notifications.filter(n => n && n.type === 'KANBAN').length,
  },

  mutations: {
    setNotifications(state, payload) {
      state.notifications = payload;
    },
    setNotifications1(state, payload) {
      state.tempNotifications1 = payload;
    },
    setNotifications2(state, payload) {
      state.tempNotifications2 = payload;
    },
    setNotifications3(state, payload) {
      state.tempNotifications3 = payload;
    },
    setNotificationLastCleared(state, payload) {
      state.notificationsLastCleared = payload;
    },
    addNotification(state, payload) {
      state.notifications.unshift(JSON.parse(payload));
    },
    emptyNotifications(state) {
      state.notifications = [];
    },
    addListener(state, payload) { // Define the addListener mutation
      state.listeners.push(payload);
    }
  },

  actions: {
    addNotification({ commit }, payload) {
      commit('addNotification', payload);
    },
    emptyNotifications({ commit }) {
      commit('emptyNotifications');
    },
    setNotificationLastCleared({ commit }, payload) {
      commit('setNotificationLastCleared', payload);
    },

    async getNotifications({ commit, rootState, dispatch }, payload) {
      try {
        const tempNotifications = [];
        const tempNotifications1 = [];
        const tempNotifications2 = [];
        const tempNotifications3 = [];

        const q1 = query(
          collection(rootState.db, 'notifications'),
          where("audience", "==", "GENERAL"),
          where("unreadList", "array-contains", payload.employeeID),
          orderBy("createdAt", "desc")
        );
        // Demo site (plan Task 6.5): the GENERAL query is not site-scoped, so the demo's Firestore rules refuse it.
        const unsubscribe1 = rootState.customer && rootState.customer.isDemo === true ? () => {} : safeSnapshot(q1, (notificationSnapshot) => {
          tempNotifications1.length = 0;
          notificationSnapshot.forEach((notification) => {
            const tempNotification = notification.data();
            tempNotification.id = notification.id;
            tempNotification.createdAt = tempNotification.createdAt ? moment(tempNotification.createdAt.toDate()).format('MM/DD/YYYY h:mm:ss a z') : '';
            tempNotifications1.push(tempNotification);
          });
          tempNotifications.length = 0;
          tempNotifications.push(...tempNotifications1, ...tempNotifications2, ...tempNotifications3);
          commit('setNotifications1', tempNotifications1);
          dispatch('debouncedUpdateNotifications', tempNotifications);
        }, { source: 'notifications/general' });

        const q2 = query(
          collection(rootState.db, 'notifications'),
          where("audience", "==", "EMPLOYEE"),
          where("employeeID", "==", payload.employeeID),
          where("unreadList", "array-contains", payload.employeeID),
          orderBy("createdAt", "desc")
        );
        const unsubscribe2 = safeSnapshot(q2, (notificationSnapshot) => {
          tempNotifications2.length = 0;
          notificationSnapshot.forEach((notification) => {
            const tempNotification = notification.data();
            tempNotification.id = notification.id;
            tempNotification.createdAt = tempNotification.createdAt ? moment(tempNotification.createdAt.toDate()).format('MM/DD/YYYY h:mm:ss a z') : '';
            tempNotifications2.push(tempNotification);
          });
          tempNotifications.length = 0;
          tempNotifications.push(...tempNotifications1, ...tempNotifications2, ...tempNotifications3);
          commit('setNotifications2', tempNotifications2);
          dispatch('debouncedUpdateNotifications', tempNotifications);
        }, { source: 'notifications/employee' });

        const q3 = query(
          collection(rootState.db, 'notifications'),
          where("audience", "==", "SITE"),
          where("siteID", "==", payload.siteID),
          where("unreadList", "array-contains", payload.employeeID),
          orderBy("createdAt", "desc")
        );
        const unsubscribe3 = safeSnapshot(q3, (notificationSnapshot) => {
          tempNotifications3.length = 0;
          notificationSnapshot.forEach((notification) => {
            const tempNotification = notification.data();
            tempNotification.id = notification.id;
            tempNotification.createdAt = tempNotification.createdAt ? moment(tempNotification.createdAt.toDate()).format('MM/DD/YYYY h:mm:ss a z') : '';
            tempNotifications3.push(tempNotification);
          });
          tempNotifications.length = 0;
          tempNotifications.push(...tempNotifications1, ...tempNotifications2, ...tempNotifications3);
          commit('setNotifications3', tempNotifications3);
          dispatch('debouncedUpdateNotifications', tempNotifications);
        }, { source: 'notifications/site' });

        // Store the unsubscribe functions to allow stopping the listeners later if needed
        commit('addListener', unsubscribe1);
        commit('addListener', unsubscribe2);
        commit('addListener', unsubscribe3);

        commit('customer/addFirestoreListener', unsubscribe1, {root: true});
        commit('customer/addFirestoreListener', unsubscribe2, {root: true});
        commit('customer/addFirestoreListener', unsubscribe3, {root: true});
      } catch (err) {
        console.log('Error while getting notifications:', err);
        dispatch('alert/setErrored', true, { root: true });
        dispatch('alert/setMessage', err, { root: true });
      }
    },

    // Mark this user's Kanban mention notifications for ONE task as read.
    // Called when the user opens a task card (EditTaskPanel.mounted). Only
    // affects the current user's unread flag on that task — other mentioned
    // teammates stay unread until they open it themselves.
    async markKanbanTaskNotificationsRead({ rootState, dispatch }, { taskId, employeeID }) {
      if (!taskId || !employeeID) return;
      try {
        const q = query(
          collection(rootState.db, 'notifications'),
          where("type", "==", "KANBAN"),
          where("kanbanTaskId", "==", taskId),
          where("unreadList", "array-contains", employeeID)
        );
        const snap = await getDocs(q);
        snap.forEach(async (n) => {
          const nRef = doc(rootState.db, 'notifications', n.id);
          try {
            await runTransaction(rootState.db, async (transaction) => {
              const nDoc = await transaction.get(nRef);
              if (!nDoc.exists) return;
              transaction.update(nRef, { "unreadList": arrayRemove(employeeID) });
            });
          } catch (error) {
            console.log("markKanbanTaskNotificationsRead tx failed:", error);
          }
        });
      } catch (err) {
        console.log('Error marking Kanban task notifications read:', err);
        dispatch('alert/setErrored', true, { root: true });
        dispatch('alert/setMessage', err, { root: true });
      }
    },

    async markAllNotificationsAsRead({ commit, rootState, dispatch }, payload) {
      if (rootState.notifications.notifications.length > 0) {
        try {
          const q = query(
            collection(rootState.db, 'notifications'),
            where("unreadList", "array-contains", payload.employeeID)
          );
          const nSnap = await getDocs(q);
          nSnap.forEach(async (n) => {
            const nRef = doc(rootState.db, 'notifications', n.id);
            await runTransaction(rootState.db, async (transaction) => {
              const nDoc = await transaction.get(nRef);
              if (!nDoc.exists) {
                throw "Document does not exist!";
              }
              transaction.update(nRef, { "unreadList": arrayRemove(payload.employeeID) });
            }).then(() => {
              //console.log("All notifications marked as Read successfully!");
            }).catch((error) => {
              console.log("Transaction failed: ", error);
            });
          });
        } catch (err) {
          console.log('Error while marking notifications read:', err);
          dispatch('alert/setErrored', true, { root: true });
          dispatch('alert/setMessage', err, { root: true });
        }
      }
      localStorage.setItem('notificationsLastCleared', moment().format('MM/DD/YYYY'));
      commit('setNotificationLastCleared', moment().format('MM/DD/YYYY'));
    },

    async deleteNotifications({ commit, rootState, dispatch }, payload) {
      try {
        dispatch('alert/setLoading', true, { root: true });
        const response = await axios.post('/delete-notifications', payload, { crossdomain: true });
        localStorage.setItem('notificationsLastCleared', moment().format('MM/DD/YYYY'));
        dispatch('alert/setLoaded', true, { root: true });
      } catch (err) {
        console.log('Error while deleting notifications:', err);
        dispatch('alert/setErrored', true, { root: true });
        dispatch('alert/setMessage', err, { root: true });
      }
    },

    // Debounced function to update notifications to prevent rapid state changes
    debouncedUpdateNotifications: _.debounce(function({ commit, state }, tempNotifications) {
      // Apply memory management like the Conversations system
      const maxNotifications = 100;
      if (tempNotifications.length > maxNotifications) {
        tempNotifications = tempNotifications.slice(0, maxNotifications);
      }

      commit('setNotifications', tempNotifications);
    }, 250), // Increased from 100ms to 250ms to match optimized Conversations system
  },
};